import { useState, type ReactNode } from "react";
import { Check, Copy, Eye, EyeOff, ShieldAlert } from "lucide-react";
import { cn } from "../../lib/cn";

export function CopyButton({ text, label = "Copy", className }: { text: string; label?: string; className?: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      className={cn("inline-flex items-center gap-1 rounded px-1.5 py-1 text-xs text-fg-3 hover:bg-panel-hover hover:text-fg", className)}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setDone(true);
          setTimeout(() => setDone(false), 1500);
        } catch {
          /* clipboard blocked */
        }
      }}
      aria-label={done ? "Copied" : label}
    >
      {done ? <Check className="size-3.5 text-good" /> : <Copy className="size-3.5" />}
      <span className="sr-only sm:not-sr-only">{done ? "Copied" : label}</span>
    </button>
  );
}

export function CodeBlock({ code, language, title, className, wrap }: { code: string; language?: string; title?: ReactNode; className?: string; wrap?: boolean }) {
  return (
    <div className={cn("overflow-hidden rounded-lg border border-border bg-bg-elev", className)}>
      <div className="flex items-center justify-between border-b border-border bg-panel px-3 py-1.5">
        <span className="font-mono text-[11px] text-fg-3">{title ?? language ?? "code"}</span>
        <CopyButton text={code} />
      </div>
      <pre className={cn("overflow-x-auto p-3 font-mono text-[12.5px] leading-relaxed text-fg", wrap && "whitespace-pre-wrap break-all")}>
        <code>{code}</code>
      </pre>
    </div>
  );
}

/**
 * One-time secret display. Once the user dismisses (or navigates away) the secret is
 * dropped from state and can never be rendered again — the server only stores a hash.
 */
export function SecretField({ secret, label = "Secret", onDismiss }: { secret: string; label?: string; onDismiss?: () => void }) {
  const [visible, setVisible] = useState(false);
  const [value, setValue] = useState<string | null>(secret);
  if (value === null) return <div className="rounded-md border border-border bg-bg-elev px-3 py-2 text-xs text-fg-3">{label} hidden permanently. Create a new one if it was lost.</div>;
  return (
    <div className="rounded-lg border border-warning/40 bg-warning/5 p-3">
      <div className="mb-2 flex items-center gap-2 text-xs text-warning">
        <ShieldAlert className="size-3.5" aria-hidden /> Copy this {label.toLowerCase()} now. It will not be shown again.
      </div>
      <div className="flex items-center gap-2">
        <code className="min-w-0 flex-1 truncate rounded bg-bg-elev px-2 py-1.5 font-mono text-xs text-fg" aria-label={label}>
          {visible ? value : value.slice(0, 12) + "•".repeat(Math.max(8, Math.min(32, value.length - 12)))}
        </code>
        <button type="button" className="rounded p-1.5 text-fg-3 hover:bg-panel-hover hover:text-fg" onClick={() => setVisible((v) => !v)} aria-label={visible ? "Hide" : "Reveal"}>
          {visible ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
        </button>
        <CopyButton text={value} />
      </div>
      <div className="mt-2 flex justify-end">
        <button
          type="button"
          className="text-xs text-fg-2 underline-offset-2 hover:text-fg hover:underline"
          onClick={() => {
            setValue(null);
            onDismiss?.();
          }}
        >
          I have stored it securely
        </button>
      </div>
    </div>
  );
}
